// Google OAuth service using AWS Cognito
import { authService } from './cognito'
import { isFeatureEnabled } from '../config/app'

// Google OAuth response types
export interface GoogleOAuthResult {
  success: boolean
  data?: any
  error?: string
}

// Google OAuth service class
class GoogleOAuthService {
  // Sign in with Google using Cognito
  public async signInWithGoogle(): Promise<GoogleOAuthResult> {
    try {
      if (!isFeatureEnabled('enableCognitoGoogleOAuth')) {
        return {
          success: false,
          error: 'Google OAuth is not enabled'
        }
      }

      const result = await authService.signInWithGoogle()
      
      if (result.success) {
        return {
          success: true
        }
      } else {
        // Handle error - it can be either string or AuthError
        const errorMessage = typeof result.error === 'string' 
          ? result.error 
          : result.error?.message || 'Google OAuth sign-in failed'
        
        return {
          success: false,
          error: errorMessage
        }
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sign in with Google'
      }
    }
  }

  // Check if Google OAuth is available
  public isAvailable(): boolean {
    return isFeatureEnabled('enableCognitoGoogleOAuth')
  }

  // Render Google sign-in button (simplified for Cognito)
  public async renderButton(element: HTMLElement, options?: {
    theme?: 'outline' | 'filled_blue' | 'filled_black'
    size?: 'large' | 'medium' | 'small'
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
    shape?: 'rectangular' | 'pill' | 'circle' | 'square'
    logo_alignment?: 'left' | 'center'
    width?: number
  }): Promise<void> {
    try {
      if (!isFeatureEnabled('enableCognitoGoogleOAuth')) {
        throw new Error('Google OAuth is not enabled')
      }

      // Create a simple button element for Cognito Google OAuth
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'cognito-google-oauth-button'
      button.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" style="margin-right: 8px;">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        ${options?.text === 'signup_with' ? 'Sign up with Google' : 
          options?.text === 'continue_with' ? 'Continue with Google' : 
          'Sign in with Google'}
      `

      // Apply styles
      button.style.cssText = `
        width: 100%;
        height: 50px;
        background-color: #ffffff;
        border: 1px solid #dadce0;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        font-weight: 500;
        color: #3c4043;
        transition: all 0.2s ease;
      `

      // Add hover effect
      button.addEventListener('mouseenter', () => {
        button.style.backgroundColor = '#f8f9fa'
        button.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
      })

      button.addEventListener('mouseleave', () => {
        button.style.backgroundColor = '#ffffff'
        button.style.boxShadow = 'none'
      })

      // Add click handler
      button.addEventListener('click', async () => {
        try {
          await this.signInWithGoogle()
        } catch (error) {
          // Google OAuth button click error
        }
      })

      // Clear existing content and append button
      element.innerHTML = ''
      element.appendChild(button)
    } catch (error) {
      // Error rendering Google sign-in button
      throw error
    }
  }
}

// Create and export singleton instance
export const googleOAuthService = new GoogleOAuthService()

// Export the service class for testing
export { GoogleOAuthService }